// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';

interface IGShop {
  id: string;
  businessName: string;
  instagramUrl: string;
  username: string;
  avatar?: string;
  category?: string;
  country?: string;
  lastSyncAt?: Date | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSelectShop: (shop: IGShop) => void;
  onAddToQueue?: (shops: IGShop[]) => void;
}

const ShopSearchDrawer: React.FC<Props> = ({ isOpen, onClose, onSelectShop, onAddToQueue }) => {
  const [shops, setShops] = useState<IGShop[]>([]);
  const [filtered, setFiltered] = useState<IGShop[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [selectedShops, setSelectedShops] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch shops with igSync enabled when drawer opens
  useEffect(() => {
    if (!isOpen || loaded) return;

    const fetchShops = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, 'shops'),
          where('igSync.enabled', '==', true)
        );
        const snapshot = await getDocs(q);
        const results: IGShop[] = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          const igUrl = data.igSync?.instagramUrl || '';
          // Extract username from URL
          const match = igUrl.match(/instagram\.com\/([^\/\?]+)/);
          const username = match ? match[1] : '';

          if (username) {
            const lastSyncAt = data.igSync?.lastSyncAt?.toDate?.() || null;
            results.push({
              id: doc.id,
              businessName: data.businessName || '',
              instagramUrl: igUrl,
              username,
              avatar: data.avatar || null,
              category: data.category?.label || data.category || '',
              country: data.country?.label || data.country || '',
              lastSyncAt
            });
          }
        });

        // Sort by lastSyncAt descending (most recent first, nulls last)
        results.sort((a, b) => {
          if (!a.lastSyncAt && !b.lastSyncAt) return 0;
          if (!a.lastSyncAt) return 1;
          if (!b.lastSyncAt) return -1;
          return b.lastSyncAt.getTime() - a.lastSyncAt.getTime();
        });
        setShops(results);
        setFiltered(results);
        setLoaded(true);
      } catch (error) {
        console.error('Error fetching IG sync shops:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchShops();
  }, [isOpen, loaded]);

  // Focus search input when drawer opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Filter shops based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFiltered(shops);
      return;
    }
    const q = searchQuery.toLowerCase();
    setFiltered(
      shops.filter(
        (s) =>
          s.businessName.toLowerCase().includes(q) ||
          s.username.toLowerCase().includes(q) ||
          s.id.toLowerCase().includes(q)
      )
    );
  }, [searchQuery, shops]);

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const toggleShopSelection = (shopId: string) => {
    setSelectedShops(prev => {
      const next = new Set(prev);
      if (next.has(shopId)) {
        next.delete(shopId);
      } else {
        next.add(shopId);
      }
      return next;
    });
  };

  const handleAddSelectedToQueue = () => {
    if (selectedShops.size === 0 || !onAddToQueue) return;
    const selected = shops.filter(s => selectedShops.has(s.id));
    onAddToQueue(selected);
    setSelectedShops(new Set());
  };

  const formatLastSync = (date: Date | null | undefined) => {
    if (!date) return 'Never';
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">IG Sync Shops</h2>
            <p className="text-xs text-gray-500">
              {shops.length} shops · {selectedShops.size} selected
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {selectedShops.size > 0 && (
              <button
                onClick={handleAddSelectedToQueue}
                className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center space-x-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Add {selectedShops.size} to Queue</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-100 shrink-0">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              placeholder="Search by name, username, or shop ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Shop List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-sm font-medium">
                {searchQuery ? 'No shops match your search' : 'No shops with IG sync found'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map((shop) => {
                const isSelected = selectedShops.has(shop.id);
                return (
                  <div
                    key={shop.id}
                    className={`w-full px-6 py-4 flex items-center space-x-4 hover:bg-purple-50 transition-colors text-left ${isSelected ? 'bg-purple-50' : ''}`}
                  >
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleShopSelection(shop.id)}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${isSelected
                        ? 'bg-purple-600 border-purple-600'
                        : 'border-gray-300 hover:border-purple-400'
                        }`}
                    >
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>

                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm shrink-0 overflow-hidden">
                      {shop.avatar ? (
                        <img src={shop.avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        shop.businessName.charAt(0).toUpperCase()
                      )}
                    </div>

                    {/* Info */}
                    <button
                      onClick={() => onSelectShop(shop)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <p className="font-bold text-gray-900 text-sm truncate">{shop.businessName}</p>
                      <p className="text-xs text-purple-600 truncate">@{shop.username}</p>
                      <div className="flex items-center space-x-2 mt-0.5">
                        <span className="text-[10px] text-gray-400 font-mono">{shop.id.slice(0, 10)}...</span>
                        {shop.category && (
                          <span className="text-[10px] text-gray-400">· {shop.category}</span>
                        )}
                      </div>
                    </button>

                    {/* Last Sync */}
                    <div className="text-right shrink-0">
                      <span className={`text-[10px] font-medium ${!shop.lastSyncAt ? 'text-red-500' : 'text-gray-400'
                        }`}>
                        {formatLastSync(shop.lastSyncAt)}
                      </span>
                    </div>

                    {/* Arrow */}
                    <svg className="w-4 h-4 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 shrink-0">
          {selectedShops.size > 0 ? (
            <button
              onClick={handleAddSelectedToQueue}
              className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add {selectedShops.size} to Queue</span>
            </button>
          ) : (
            <p className="text-xs text-gray-400 text-center">
              {loading ? 'Loading...' : `Showing ${filtered.length} of ${shops.length} shops`}
            </p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default ShopSearchDrawer;
